import { cn } from '~/shadcn/lib/utils'
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '~/shadcn/ui/accordion'
import { getFileType } from '~/@lib/utils/files.utils'
import { DownloadIcon } from '~/@mid_ui/icons/DownloadIcon'
import { DocumentIcon } from '~/@mid_ui/icons/DocumentIcon'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { useAccordion } from '~/@mid_ui/accordion/useAccordion'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { HeadingText } from '~/@pages_investing/project-detail/project-detail_ui/detail-heading/DetailHeading'
import { useProjectAttachmentDownload } from './useProjectAttachmentDownload'
import { Button } from '~/shadcn/ui/button/button'
import { Spinner } from '~/shadcn/ui/spinner'

type Props = {
  project: ProjectSchema
}

export function ProjectAttachments({ project }: Props) {
  const isDesktop = useIsDesktop()
  const { toggleSection } = useAccordion()
  const { downloadFile, isDownloadingFileId } = useProjectAttachmentDownload()

  if (!project.attachments.length) return null

  const handleDownloadAll = () => {
    project.attachments.forEach((item) => {
      downloadFile({
        fileId: item._id,
        filename: item.name,
        token: item.token ?? '',
        projectId: project._id,
      })
    })
  }

  return (
    <div className="flex flex-col">
      {isDesktop ? (
        <div
          className="px-5 pt-[30px] pb-5 lg:px-10 lg:py-5"
          style={{ borderBottom: '1px solid #969696' }}>
          <div className="flex flex-row justify-between items-center">
            <TitleElement />
            <div className="flex flex-row items-center gap-1">
              {isDownloadingFileId != null ? (
                <Spinner />
              ) : (
                <DownloadIcon className="w-5 h-5 text-accent" />
              )}

              <button
                onClick={handleDownloadAll}
                className="border-0 bg-transparent text-accent text-base font-semibold cursor-pointer p-0">
                Stiahnuť všetko
              </button>
            </div>
          </div>
        </div>
      ) : (
        <AccordionItem value="attachments" className="border-0">
          <div
            className={cn(
              'px-5 pt-7 pb-5',
              'z-1',
              'sticky',
              'top-0',
              'bg-[rgb(245,245,245)]'
            )}
            style={{ borderBottom: '1px solid #969696' }}>
            <AccordionTrigger
              className="w-full p-0 h-auto hover:no-underline hover:bg-transparent focus-visible:ring-0"
              onClick={() => toggleSection('attachments')}>
              <div className="flex flex-row justify-between items-center w-full">
                <TitleElement />
              </div>
            </AccordionTrigger>
          </div>

          <AccordionContent className="p-5">
            <div className="flex flex-row justify-end gap-1 pb-8">
              <DownloadIcon className="w-5 h-5 text-accent" />
              <button
                onClick={handleDownloadAll}
                className="border-0 bg-transparent text-accent text-base font-semibold cursor-pointer p-0">
                Stiahnuť všetko
              </button>
            </div>
            <div
              className={cn(
                'flex flex-col justify-start',
                '[&>a]:py-3',
                'lg:[&>a]:py-1.5 lg:[&>a]:px-10',
                '[&>a:hover]:bg-[#C7E1FF]',
                'pb-[50px] pt-2.5',
                '[&_a_.download-icon]:hidden',
                '[&_a:hover_.download-icon]:block'
              )}
              style={{ borderBottom: '1px solid #969696' }}>
              {project.attachments.map((item) => (
                <Button
                  key={`ProjectAttachment-${item._id}`}
                  onClick={() =>
                    downloadFile({
                      fileId: item._id,
                      filename: item.name,
                      token: item.token ?? '',
                      projectId: project._id,
                    })
                  }
                  variant="ghost"
                  disabled={isDownloadingFileId != null}
                  className="group flex flex-row justify-start gap-2 px-0 py-2 h-auto text-left focus:bg-accent/20 rounded-none">
                  <div className="flex flex-row justify-start items-center gap-0.5">
                    <DocumentIcon className="w-6! h-6! text-accent" />

                    <HeadingText className="text-accent text-base">
                      {item.title || item.name} {getFileType(item.extension)}
                    </HeadingText>
                  </div>
                  {isDownloadingFileId === item._id ? (
                    <Spinner />
                  ) : (
                    <DownloadIcon className="download-icon w-5 h-5 text-accent opacity-0 group-focus:opacity-100 transition-opacity" />
                  )}
                </Button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {isDesktop && (
        <div className="border-b border-border">
          <div
            className={cn(
              'flex flex-col',
              'py-10',
              '[&_a_.download-icon]:hidden',
              '[&_a:hover_.download-icon]:block'
            )}>
            {project.attachments.map((item) => (
              <Button
                key={`ProjectAttachment-${item._id}`}
                onClick={() =>
                  downloadFile({
                    fileId: item._id,
                    filename: item.name,
                    token: item.token ?? '',
                    projectId: project._id,
                  })
                }
                variant="ghost"
                size="xs"
                disabled={isDownloadingFileId != null}
                className="group flex flex-row justify-between items-center gap-4 px-10 py-2 h-auto rounded-none hover:bg-accent/20">
                <div className="flex flex-row items-center gap-0.5">
                  <DocumentIcon className="w-6! h-6! text-accent" />
                  <HeadingText className="text-accent text-base/tight text-left">
                    {item.title || item.name} {getFileType(item.extension)}
                  </HeadingText>
                </div>
                {isDownloadingFileId === item._id ? (
                  <Spinner />
                ) : (
                  <DownloadIcon className="download-icon w-5! h-5! text-accent shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const TitleElement = () => (
  <h2 className="text-2xl lg:text-[32px] font-semibold">
    Dokumenty na stiahnutie
  </h2>
)
